import React from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import GameSliderContainer from 'components/GameSliderContainer'
import GetSliderGames from 'components/Lobby/GetSliderGames'
import useFilterGames from 'components/Lobby/hooks/useFilterGames'
import useFavoriteGames from 'hooks/useFavoriteGames'
import HomeGameBanners from 'components/Lobby/HomeGameBanners'
import NoDataAvailable from 'components/NoDataAvailable'
import GameCategories from 'components/Lobby/GameCategories'
import FavoriteList from 'components/Lobby/Favorite'
import RecentGames from 'components/Lobby/RecentGames'
import './styles/index.scss'
import '../../../../../components/Lobby/AllGamesLobby/styles/index.scss'
import { GAME_CATEGORY_EXTRA_TAB_KEYS } from 'constants/uiConfig'

/**
 * HomePageGamesV2 displays the game grid on the homepage (variant v2)
 * Implements tabbed subcategories using TabsView. Each subcategory appears
 * as a tab and renders a single games slider below.
 *
 * @param {Object} props - Component props
 * @param {string} props.selectedFilter - Currently selected game filter
 * @param {function} props.setSelectedFilter - Function to update the selected filter
 * @returns {JSX.Element} React component that renders the games grid section
 */
const HomePageGamesV2 = ({ selectedFilter, setSelectedFilter }) => {
  const navigate = useNavigate()
  const {
    gameCategory,
    languageData,
    changeMenuTab,
    gameSubCategory,
    selectedLanguageCode,
    subCatLoading,
    selectedMenu
  } = useFilterGames({ category: true, selectedFilter, setSelectedFilter })
  const { favoriteGamesList, toggleFavorite } = useFavoriteGames()
  const uiGeneral = useSelector(state => state?.uiConfig?.general)
  const homePageSliderVariant = uiGeneral?.homePageSliderVariant

  const subcategories = React.useMemo(() => gameSubCategory?.rows || [], [gameSubCategory?.rows])

  const defaultActiveKey = React.useMemo(() => (subcategories?.[0]?.id != null
    ? subcategories?.[0]?.id
    : undefined), [subcategories])

  const [activeKey, setActiveKeyy] = React.useState(defaultActiveKey)

  const setActiveKey = (...params) => {
    setActiveKeyy(...params)
  }

  React.useEffect(() => {
    setActiveKey(selectedFilter?.subCategoryId || defaultActiveKey)
  }, [defaultActiveKey, selectedFilter])

  React.useEffect(() => {
    setSelectedFilter(selectedFilter => ({
      ...selectedFilter,
      subCategoryId: defaultActiveKey
    }))

    return () => {
      setSelectedFilter(selectedFilter => ({
        ...selectedFilter,
        category: [],
        subCategoryId: ''
      }))
    }
  }, [defaultActiveKey])

  const activeSubcategory = React.useMemo(
    () => subcategories?.find(subCategory => {
      return String(subCategory?.id) === String(activeKey)
    }),
    [subcategories, activeKey]
  )

  return (
    <>
      <div className='home-page-games home-page-games--v2'>
        <HomeGameBanners />
        <div className='home-page-games__title'>{languageData?.moreGames || 'More Games'}</div>

        <div className='filters-content casino-category-bar'>
          <div className='game-type-container'>
            <div className='game-type-slider'>
              <GameCategories
                selectedFilter={selectedFilter}
                setSelectedFilter={setSelectedFilter}
                selectedMenu={selectedMenu}
                selectedLanguageCode={selectedLanguageCode}
                gameSubCategory={gameSubCategory}
                hideAllGamesTab={true}
              />
            </div>
          </div>
        </div>

        {selectedFilter?.category?.[0] === GAME_CATEGORY_EXTRA_TAB_KEYS?.RECENT_GAMES ? <RecentGames /> : null}
        {selectedFilter?.category?.[0] === GAME_CATEGORY_EXTRA_TAB_KEYS?.FAVORITES ? <FavoriteList /> : null}
        {!subCatLoading && activeSubcategory
          ? (
            activeSubcategory?.TenantGames?.length > 0
              ? (
                <GameSliderContainer>
                  <GetSliderGames
                    variant={homePageSliderVariant}
                    categorySlug={gameCategory?.casinoCategories?.rows?.[0]?.slug}
                    games={activeSubcategory?.TenantGames}
                    name={activeSubcategory?.name}
                    isLabeled={activeSubcategory?.isLabelledCategory}
                    labelIconUrl={activeSubcategory?.labelIconUrl}
                    imageUrl={activeSubcategory?.imageUrl}
                    selectedLanguageCode={selectedLanguageCode}
                    navigate={navigate}
                    changeMenuTab={changeMenuTab}
                    gameCategory={gameCategory}
                    languageData={languageData}
                    toggleFavorite={toggleFavorite}
                    favoriteGamesList={favoriteGamesList}
                  />
                </GameSliderContainer>
              )
              : (
                <div className='no-data-found' dir='ltr'>
                  <NoDataAvailable errorMessage={languageData?.casinoNoGamesFound} />
                </div>
              )
          )
          : null}
      </div>
    </>
  )
}

export default HomePageGamesV2
